import { isValidCron } from 'cron-validator';

import {
  DayState,
  FormState,
  HourState,
  MinuteState,
  MonthState,
  Weekday,
  WeekState,
} from '../StandardInputsTab/_types';

const stringToCronTimePart = (value: string) => {
  const numVal = Number(value);
  if (isNaN(numVal)) {
    return undefined;
  }
  if (numVal < 10) {
    return `0${numVal}`;
  }
  return value;
};

const weekDayToName: Record<string, Weekday> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

type CronParts = {
  minutes: string;
  hours: string;
  day: string;
  month: string;
  weekDay: string;
};
const tryToMatchWeek = ({
  minutes,
  hours,
  day,
  month,
  weekDay,
}: CronParts): WeekState | undefined => {
  if (day !== '*') {
    return;
  }
  if (month !== '*') {
    return;
  }
  const parsedHours = stringToCronTimePart(hours);
  if (!parsedHours) {
    return;
  }
  const parsedMinutes = stringToCronTimePart(minutes);
  if (!parsedMinutes) {
    return;
  }
  if (weekDay.includes('/')) {
    return;
  }
  const days: Weekday[] = [];
  if (weekDay.includes(',')) {
    const weekDays = weekDay.split(',');
    for (const part of weekDays) {
      const day = weekDayToName[part];
      if (!day) {
        return;
      }
      days.push(day);
    }
  } else if (!weekDayToName[weekDay]) {
    return;
  } else {
    days.push(weekDayToName[weekDay]);
  }
  return {
    type: 'Week',
    repeatEvery: 1,
    at: `${parsedHours}:${parsedMinutes}`,
    on: days,
  };
};

const tryToMatchDay = ({
  minutes,
  hours,
  day,
  month,
  weekDay,
}: CronParts): DayState | undefined => {
  if (weekDay !== '*') {
    return;
  }
  if (month !== '*') {
    return;
  }
  if (!day.startsWith('*') || day.includes(',')) {
    return;
  }
  let numericDay = 1;
  if (day.includes('/')) {
    numericDay = Number(day.split('/')[1]);
    if (isNaN(numericDay)) {
      return;
    }
  }
  const parsedHours = stringToCronTimePart(hours);
  if (!parsedHours) {
    return;
  }
  const parsedMinutes = stringToCronTimePart(minutes);
  if (!parsedMinutes) {
    return;
  }
  return {
    type: 'Day',
    repeatEvery: numericDay,
    at: `${parsedHours}:${parsedMinutes}`,
  };
};

const tryToMatchHour = ({
  minutes,
  hours,
  day,
  month,
  weekDay,
}: CronParts): HourState | undefined => {
  if (day !== '*') {
    return;
  }
  if (month !== '*') {
    return;
  }
  if (weekDay !== '*') {
    return;
  }
  const parsedMinutes = stringToCronTimePart(minutes);
  if (!parsedMinutes) {
    return;
  }
  if (!hours.match(/\*\/\d+/)) {
    return;
  }
  const [, repeatEvery] = hours.split('/');
  const numericHours = Number(repeatEvery);
  if (isNaN(numericHours)) {
    return;
  }
  return {
    type: 'Hour',
    repeatEvery: numericHours,
    startAtMinute: parsedMinutes,
  };
};

const tryToMatchMinute = ({
  minutes,
  hours,
  day,
  month,
  weekDay,
}: CronParts): MinuteState | undefined => {
  if (weekDay !== '*') {
    return;
  }
  if (month !== '*') {
    return;
  }
  if (day !== '*') {
    return;
  }
  const matchedHours = hours.match(
    /(?<startFromHour>[0-9]|1[0-9]|2[0-3])?-(?<endAtHour>23)?/
  )?.groups;
  if (!matchedHours?.startFromHour || !matchedHours?.endAtHour) {
    return;
  }
  const startFromHour = stringToCronTimePart(matchedHours.startFromHour);
  if (!startFromHour) {
    return;
  }

  let startAtMinute: string;
  let repeatEvery: number;

  if (minutes === '*') {
    startAtMinute = '00';
    repeatEvery = 1;
  } else if (minutes.includes('/')) {
    const parts = minutes.split('/');
    if (parts.length !== 2) {
      return;
    }
    const [startingMin, repeatEveryMin] = parts;
    if (startingMin === '*') {
      startAtMinute = '00';
    } else {
      const parsedMin = stringToCronTimePart(startingMin);
      if (!parsedMin) {
        return;
      }
      startAtMinute = parsedMin;
    }
    const parsedMin = Number(repeatEveryMin);
    if (isNaN(parsedMin)) {
      return;
    }
    repeatEvery = parsedMin;
  } else {
    return;
  }

  return {
    type: 'Minute',
    startAt: `${startFromHour}:${startAtMinute}`,
    repeatEvery,
  };
};

const tryToMatchMonth = ({
  minutes,
  hours,
  day,
  month,
  weekDay,
}: CronParts): MonthState | undefined => {
  if (weekDay !== '*') {
    return;
  }
  const parsedHours = stringToCronTimePart(hours);
  if (!parsedHours) {
    return;
  }
  const parsedMinutes = stringToCronTimePart(minutes);
  if (!parsedMinutes) {
    return;
  }
  let repeatEvery = 1;
  if (month.includes('/')) {
    const parts = month.split('/');
    if (parts.length !== 2) {
      return;
    }
    const [asterisk, everyNthMonth] = parts;
    if (asterisk !== '*') {
      return;
    }
    const numericMonth = Number(everyNthMonth);
    if (isNaN(numericMonth)) {
      return;
    }
    repeatEvery = numericMonth;
  } else if (month !== '*') {
    return;
  }
  if (day.includes(',')) {
    const days = day.split(',');
    const parsedDays: Set<string> = new Set();
    for (const day of days) {
      const numericDay = Number(day);
      if (isNaN(numericDay) || numericDay < 1 || numericDay > 31) {
        return;
      }
      parsedDays.add(day);
    }
    if (!parsedDays.size) {
      return;
    }
    return {
      type: 'Month',
      repeatEvery,
      onDay: Array.from(parsedDays),
      at: `${parsedHours}:${parsedMinutes}`,
    };
  } else {
    const numericDay = Number(day);
    if (isNaN(numericDay) || numericDay < 1 || numericDay > 31) {
      return;
    }
    return {
      type: 'Month',
      repeatEvery,
      onDay: [day],
      at: `${parsedHours}:${parsedMinutes}`,
    };
  }
};

export const cronToFormState = (cron: string): FormState | undefined => {
  cron = cron.replace(/\s+/g, ' ');
  if (
    !isValidCron(cron, {
      alias: true,
      seconds: false,
      allowBlankDay: false,
      allowSevenAsSunday: false,
    })
  ) {
    return;
  }
  const parts = cron.split(' ');
  const [minutes, hours, day, month, weekDay] = parts;
  const cronParts: CronParts = {
    minutes,
    hours,
    day,
    month,
    weekDay,
  };

  return (
    tryToMatchWeek(cronParts) ||
    tryToMatchDay(cronParts) ||
    tryToMatchHour(cronParts) ||
    tryToMatchMinute(cronParts) ||
    tryToMatchMonth(cronParts)
  );
};
